import StructureSvgLoader from "../../js/repository/StructureSvgLoader.js";

export default class UploadStructureHtml {
    #rootEl;
    #callbacks = {
        onFileAttached: [],
        onZoomInClicked: [],
        onImportClicked: []
    }

    init(rootEl) {
        this.#rootEl = rootEl;
    }

    onFileAttached(callback) {
        this.#callbacks.onFileAttached.push(callback);
    }

    onImportClicked(callback) {
        this.#callbacks.onImportClicked.push(callback);
    }

    onZoomInClicked(callback) {
        this.#callbacks.onZoomInClicked.push(callback);
    }

    /**
     * @param {boolean} isReadOnly
     */
    show({isReadOnly} = {isReadOnly: false}) {
        this.#rootEl.innerHTML = this.#template(isReadOnly);
        this.#setEventListeners();
    }

    /**
     * @param {string} svgXml
     * @param {boolean} isReadOnly
     */
    showStructure(svgXml, isReadOnly) {
        this.#getComponentBodyEl().innerHTML = this.#structureTemplate(svgXml, isReadOnly);
        this.#getZoomInIconEl().addEventListener('click', this.#onZoomInClicked.bind(this));
    }

    shake() {
        this.#rootEl.classList.add('shaking');
        setTimeout(() => {
            this.#rootEl.classList.remove('shaking')
        }, 1000)
    }

    structureFadeIn() {
        this.#setStructureOpacity(1);
    }

    structureFadeOut() {
        this.#setStructureOpacity(.3);
    }

    setPointerEventsDisabled(isDisabled) {
        if (this.#rootEl != null)
            this.#rootEl.style.pointerEvents = isDisabled ? 'none' : 'all';
    }

    #onFileAttached(event) {
        try {
            this.#callbacks.onFileAttached.forEach(callback => callback(event.target.files));
        } finally {
            // w/o clearing the value, Chrome will not dispatch again for the 2nd time:
            // https://stackoverflow.com/a/11280934
            event.target.value = '';
        }
    }

    #onZoomInClicked() {
        const svgXml = this.#getStructureSvgEl().outerHTML;
        this.#callbacks.onZoomInClicked.forEach(callback => callback(svgXml));
    }

    #setStructureOpacity(opacity) {
        const svgEl = this.#getStructureSvgEl();
        svgEl && (svgEl.style.opacity = opacity);
    }

    #getStructureSvgEl() {
        return this.#rootEl.querySelector('.structure-container svg');
    }

    #setEventListeners() {
        const uploadInput = this.#getUploadFileInputEl();
        if (uploadInput) {
            uploadInput.addEventListener('change', this.#onFileAttached.bind(this))
            // Prevent closing the dialog when the user cancels the file selection dialog
            uploadInput.addEventListener('cancel', (evt) => evt.stopPropagation());
        }
        const importEl = this.#getImportEl();
        if (importEl)
            importEl.addEventListener('click', () => this.#callbacks.onImportClicked.forEach(callback => callback()));
    }

    #getComponentBodyEl() {
        return this.#rootEl.querySelector('.upload-structure-component__body');
    }

    #getUploadFileInputEl() {
        return this.#rootEl.querySelector('#upload-file-input');
    }

    #getImportEl() {
        return this.#rootEl.querySelector('.drop-structure__import');
    }

    #getZoomInIconEl() {
        return this.#rootEl.querySelector('.icon--zoom-in');
    }

    /**
     * @param {string} svgXml
     * @param {boolean} isReadOnly
     * @returns {string}
     */
    #structureTemplate(svgXml, isReadOnly) {
        return `
            <div class="structure-container">
                <div class="structure-container__icons">
                    <i class="material-symbols-outlined icon--zoom-in">zoom_in</i>                    
                </div>
                ${svgXml}
                ${isReadOnly?'':
                `<div class="structure-container__hint">
                    <span>To change drop or paste structure here or </span>
                    <label for="upload-file-input">browse files</label>
                </div>`}
            </div>`;
    }

    #uploadStructureMessageTemplate() {
        return `
            <div class="drop-structure">
                <svg class="drop-structure__svg" xmlns="http://www.w3.org/2000/svg" width="96px" height="96px" viewBox="0 0 128 128">
                    <path 
                        d="M91.215,90H77c-0.553,0-1-0.447-1-1s0.447-1,1-1h14.215C103.431,88,113,78.684,113,66.79c0-11.908-9.772-21.596-21.785-21.596c-1.498,0-3.062,0.188-4.785,0.575c-0.418,0.094-0.842-0.086-1.066-0.443c-5.187-8.244-14.08-13.166-23.789-13.166c-13.957,0-25.628, 10.019-27.751,23.823c-0.083,0.542-0.584,0.928-1.132,0.837c-0.82-0.119-1.551-0.177-2.235-0.177C21.789,56.644,15,63.323,15,71.852C15,80.604,22.078,88,30.456,88H52c0.553,0,1,0.447,1,1s-0.447,1-1,1H30.456C20.994,90,13,81.689,13,71.852c0-9.649,7.668-17.208,17.456-17.208c0.496,0,1.01,0.026,1.553,0.079C34.664,40.432,46.947,30.16,61.574,30.16c10.167,0,19.499,5.038,25.103,13.513c1.604-0.321,3.095-0.479,4.538-0.479C104.33,43.194,115,53.779,115,66.79C115,79.805,104.553,90,91.215,90z"
                    	fill="#808080"/>
                    <path 
                        d="M64.589,97.825c-0.553,0-1-0.447-1-1V62.223L51.992,74.835c-0.374,0.408-1.006,0.434-1.413, 0.06c-0.406-0.374-0.434-1.007-0.06-1.413l13.333-14.5c0.279-0.304,0.716-0.404,1.1-0.255s0.637,0.52, 0.637,0.932v37.167C65.589,97.378,65.142,97.825,64.589,97.825z"
                    	fill="#808080"/>
                    <path 
                        d="M77.922,75.325c-0.271,0-0.543-0.11-0.74-0.327L63.849,60.331c-0.371-0.409-0.341-1.041, 0.067-1.413c0.408-0.37,1.042-0.341,1.413,0.067l13.333,14.667c0.371,0.409,0.341,1.041-0.067, 1.413C78.403,75.239,78.162,75.325,77.922,75.325z"
                    	fill="#808080"/>
                </svg>
                <div class="drop-structure__message">
                    <span>Drop or paste structure here</span>
                    <span>or</span>
                    <div class="drop-structure__actions">
                        <label for="upload-file-input">Browse files</label>
                        <span>or</span>
                        <span class="drop-structure__import">Import</span>
                    </div>
                </div>
            </div>`;
    }


    /**
     * @param {boolean} isReadOnly
     * @returns {string}
     */
    #template(isReadOnly) {
        return `
            <label class="visually-hidden"><input type="file" id="upload-file-input"/></label>
            <div class="upload-structure-component__body">
                ${isReadOnly? StructureSvgLoader.NO_STRUCTURE_SVG : this.#uploadStructureMessageTemplate()}
            </div>`
    }
}